import React, { useState } from "react";
import { Spin } from "antd";
import { FaBoxOpen, FaImage } from "react-icons/fa";
import { OrderProductItemType } from "../../../interfaces/admin.interface";
import ShowImage from "../show-image/ShowImage";
import styles from "./OrderProductsTable.module.scss";

interface OrderProductsTableProps {
  products: OrderProductItemType[];
  isLoading: boolean;
  orderId?: number | string | null;
}

const OrderProductsTable: React.FC<OrderProductsTableProps> = ({
  products,
  isLoading,
  orderId,
}) => {
  const [selectedImage, setSelectedImage] = useState<string>("");
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState<boolean>(false);

  const handleOpenImagePreview = (imageUrl: string) => {
    if (!imageUrl) return;
    setSelectedImage(imageUrl);
    setIsPreviewModalOpen(true);
  };

  const handleKeyDownImagePreview = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    imageUrl: string
  ) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleOpenImagePreview(imageUrl);
  };

  if (isLoading) {
    return (
      <div className={styles.loading_container}>
        <Spin size="large" />
        <span className={styles.loading_text}>Məhsullar yüklənir...</span>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className={styles.empty_container}>
        <FaBoxOpen className={styles.empty_icon} />
        <p className={styles.empty_title}>Sifarişə aid məhsul tapılmadı</p>
        {orderId ? (
          <span className={styles.empty_sub}>Sifariş ID: #{orderId}</span>
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.products_container}>
      <div className={styles.header_row}>
        <h4 className={styles.title}>
          <FaBoxOpen className={styles.icon} />
          <span>Sifariş edilən məhsullar</span>
        </h4>
        <span className={styles.count_badge}>Cəmi: {products.length} məhsul</span>
      </div>

      <div className={styles.table_scroll}>
        <table className={styles.products_table}>
          <thead>
            <tr>
              <th className={styles.col_number}>№</th>
              <th className={styles.col_image}>Şəkil</th>
              <th className={styles.col_name}>Məhsul adı</th>
              <th className={styles.col_qty}>Sayı</th>
              <th className={styles.col_price}>Vahid qiymət</th>
              <th className={styles.col_price}>Qiymət</th>
              <th className={styles.col_price}>Variant qiyməti</th>
              <th className={styles.col_percent}>Faiz</th>
              <th className={styles.col_price}>Endirimli vahid qiymət</th>
              <th className={styles.col_price}>Endirimli qiymət</th>
            </tr>
          </thead>
          <tbody>
            {products.map((item, index) => {
              return (
                <tr
                  key={`${item.product_id}-${index}`}
                  tabIndex={0}
                  aria-label={`${item.name}, Say: ${item.quantity}`}
                >
                  <td className={styles.cell_number}>{index + 1}</td>
                  <td className={styles.cell_image}>
                    {item.image ? (
                      <button
                        type="button"
                        tabIndex={0}
                        aria-label={`Böyüt: ${item.name}`}
                        onClick={() => handleOpenImagePreview(item.image)}
                        onKeyDown={(e) =>
                          handleKeyDownImagePreview(e, item.image)
                        }
                        className={styles.thumb_btn}
                      >
                        <img
                          src={item.image}
                          alt={item.name}
                          loading="lazy"
                        />
                      </button>
                    ) : (
                      <div className={styles.thumb_placeholder}>
                        <FaImage size={16} />
                      </div>
                    )}
                  </td>
                  <td className={styles.cell_name}>
                    <div className={styles.name_box}>
                      <span className={styles.product_name}>{item.name}</span>
                      <span className={styles.product_id_badge}>
                        ID: #{item.product_id}
                      </span>
                    </div>
                  </td>
                  <td className={styles.cell_qty}>
                    {item.quantity}
                  </td>
                  <td className={styles.cell_unit_price}>
                    {item.unit_price ? `${item.unit_price} ₼` : "-"}
                  </td>
                  <td className={styles.cell_price}>
                    {item.price ? `${item.price} ₼` : "-"}
                  </td>
                  <td className={styles.cell_variant_price}>
                    {item.variant_price ? `${item.variant_price} ₼` : "-"}
                  </td>
                  <td className={styles.cell_percent}>
                    {item.percent ? `${item.percent}%` : "-"}
                  </td>
                  <td className={styles.cell_discount_unit_price}>
                    {item.discounted_unit_price ? `${item.discounted_unit_price} ₼` : "-"}
                  </td>
                  <td className={styles.cell_discount_price}>
                    {item.discounted_price ? `${item.discounted_price} ₼` : "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ShowImage
        showImageModal={isPreviewModalOpen}
        setShowImagesModal={setIsPreviewModalOpen}
        image={selectedImage}
      />
    </div>
  );
};

export default OrderProductsTable;
